import { FIGHTS_PER_SEASON_FOR_EACH_USER } from '../constants/tournament.constant';
import { Season } from '../models/season.model';
import {
  CleanTournament,
  FrontendTournament,
  FrontendTournamentWithDetails,
  TournamentParticipant,
} from '../models/tournament.model';
import { CODE_TO_COUNTRY } from './country.utils';
import { parseSafeDate } from './date.utils';

/**
 * Calculate seasonIds for a tournament based on available seasons
 */
export const calculateTournamentSeasonIds = (
  allSeasons: Season[],
  maxSeasons: number,
  startDate: Date,
): string[] => {
  // First sort all seasons by start date (ascending)
  const sortedSeasons = [...allSeasons].sort(
    (a, b) =>
      parseSafeDate(a.startDate).getTime() -
      parseSafeDate(b.startDate).getTime(),
  );
  // Find the index of the first season whose end date is after the tournament's start date
  const startIndex = sortedSeasons.findIndex(
    (season) => parseSafeDate(season.endDate) >= parseSafeDate(startDate),
  );
  // If no suitable season found, return empty array
  if (startIndex === -1) return [];

  // Take maxSeasons seasons starting from the identified season
  return sortedSeasons
    .slice(startIndex, startIndex + maxSeasons)
    .map((season) => season.seasonId);
};

/**
 * Calculate the end date of a tournament based on its seasons
 NOTE: This will only work when tournament end date equals the last event date 
*/
export const calculateTournamentEndDate = (
  seasonIds: string[],
  allSeasons: Season[],
): Date | undefined => {
  if (!seasonIds.length || !allSeasons.length) return undefined;

  const tournamentSeasons = allSeasons.filter((season) =>
    seasonIds.includes(season.seasonId),
  );
  if (!tournamentSeasons.length) return undefined;

  // Find the latest end date among all seasons
  return tournamentSeasons.reduce((latestDate, season) => {
    const seasonEndDate = parseSafeDate(season.endDate);
    return seasonEndDate > latestDate ? seasonEndDate : latestDate;
  }, new Date(0));
};

/**
 * Check if a tournament is active
 */
export const isTournamentActive = (
  tournament: CleanTournament,
  endDate: Date | undefined,
): boolean => {
  return !endDate || new Date() < parseSafeDate(endDate);
};

/**
 * Convert Tournament to FrontEndTournament with calculated properties
 */
export const toFrontendTournament = (
  tournament: CleanTournament,
  allSeasons: Season[],
): FrontendTournament => {
  // Calculate seasonIds
  const seasonIds = calculateTournamentSeasonIds(
    allSeasons,
    tournament.numberOfSeasons,
    tournament.startDate,
  );

  // Calculate end date & is active  based on seasons. For global annual tournament, end date is the last day of the year for
  // the same year as the tournament start date
  let endDate = undefined;
  if (
    seasonIds.length === 0 &&
    isFMMAPublicTournament(
      tournament.name,
      tournament.creatorName,
      tournament.private,
    )
  ) {
    // Set endDate to last day of the same year as tournament start date
    const startYear = parseSafeDate(tournament.startDate).getFullYear();
    endDate = new Date(startYear, 11, 31); // Month 11 = December, day 31
  } else {
    endDate = calculateTournamentEndDate(seasonIds, allSeasons);
  }
  const isActive = isTournamentActive(tournament, endDate);
  return {
    ...tournament,
    seasonIds,
    endDate,
    isActive,
  };
};

/**
 * Calculates the total points for a participant across all seasons
 */
export const calculateTotalPoints = (
  participant: TournamentParticipant,
): number => {
  return Object.values(participant.seasonResults).reduce(
    (total, seasonResult) => total + seasonResult.points,
    0,
  );
};

/**
 * Calculates the total fights completed for a participant across all seasons
 * Only counts fights that have points (i.e., have been performed)
 */
export const calculateTotalFights = (
  participant: TournamentParticipant,
): number => {
  return Object.values(participant.seasonResults).reduce(
    (total, seasonResult) => {
      // Only count as completed if the season has points (fights were performed)
      return total + (seasonResult.points > 0 ? seasonResult.totalFights : 0);
    },
    0,
  );
};

/**
 * Overall Ranking tab: Calculates the (with data details) enriched overall rankings for participants
 */
export const getEnrichedOverallRankings = (
  participants: TournamentParticipant[],
  totalSeasons: number,
  currentUsername?: string,
  tournamentEnded?: boolean,
): Array<{
  username: string;
  profilePicture?: string;
  nationality?: string;
  points: number;
  completedFights: number;
  totalPossibleFights: number;
  participatedSeasons: number;
  isCurrentUser: boolean;
  isWinner: boolean;
  rank: number;
}> => {
  // First sort participants by points
  const sortedParticipants = [...participants].sort(
    (a, b) => calculateTotalPoints(b) - calculateTotalPoints(a),
  );

  // Determine if there's a winner (tournament completed and has participants)
  const hasWinner = tournamentEnded && participants.length > 0;

  // Map and enrich with UI-specific properties
  return sortedParticipants.map((participant, index) => {
    const totalPoints = calculateTotalPoints(participant);
    const completedFights = calculateTotalFights(participant);
    // Only count seasons where the user has points > 0 (actually performed fights)
    const participatedSeasons = Object.values(participant.seasonResults).filter(
      (seasonResult) => seasonResult.points > 0,
    ).length;
    // Total possible fights should be based on tournament's total seasons
    const totalPossibleFights = totalSeasons * FIGHTS_PER_SEASON_FOR_EACH_USER;

    return {
      username: participant.username,
      profilePicture: participant.profilePicture,
      nationality: participant.nationality,
      points: totalPoints,
      completedFights,
      totalPossibleFights,
      participatedSeasons,
      isCurrentUser: participant.username === currentUsername,
      isWinner: Boolean(hasWinner && index === 0 && totalPoints > 0),
      rank: index + 1,
    };
  });
};

/**
 * Season Results tab: Calculates the (with data details) enriched season-specific results for participants
 */
export const getEnrichedSeasonResults = (
  participants: TournamentParticipant[],
  seasonId: string,
  currentUsername?: string,
  allSeasons?: Season[],
): Array<{
  username: string;
  profilePicture?: string;
  nationality?: string;
  seasonPoints: number;
  participatedFights: number;
  isCurrentUser: boolean;
  isWinner: boolean;
  rank: number;
}> => {
  // Extract and enrich season-specific results
  const seasonResults = participants
    .map((participant) => {
      const seasonResult = participant.seasonResults.find(
        (sr) => sr.seasonId === seasonId,
      ) || { points: 0, totalFights: 0, seasonId };

      return {
        username: participant.username,
        profilePicture: participant.profilePicture,
        nationality: participant.nationality,
        seasonPoints: seasonResult.points,
        // Only count fights as participated if the user has points (i.e., fights were completed)
        participatedFights:
          seasonResult.points > 0 ? seasonResult.totalFights : 0,
        isCurrentUser: participant.username === currentUsername,
        isWinner: false, // Will be set after sorting
        rank: 0, // Will be set after sorting
      };
    })
    .sort((a, b) => b.seasonPoints - a.seasonPoints);

  // Find the season to check if it's ended
  const season = allSeasons?.find((s) => s.seasonId === seasonId);
  const today = new Date();

  // Determine if season has a winner based on end date and participants
  const seasonEnded = season && new Date(season.endDate) < today;
  const hasParticipants = seasonResults.length > 0;
  const shouldHaveWinner = seasonEnded && hasParticipants;

  // Set rank and winner status
  return seasonResults.map((result, index) => ({
    ...result,
    isWinner:
      (shouldHaveWinner && index === 0 && result.seasonPoints > 0) || false,
    rank: index + 1,
  }));
};

/**
 * Create new tournament modal: a function to check if today is between two seasons
 */
export const checkIsBetweenSeasons = (seasons: Season[], today: Date) => {
  // No seasons means not between seasons
  if (seasons.length === 0) return false;

  // Check if today is not in any active season
  const notInActiveSeason = !seasons.some((season) => {
    const seasonStart = new Date(season.startDate);

    // only works if end date of season is equal to last event date of season
    const seasonEnd = season.endDate ? new Date(season.endDate) : null;

    // Create a date for the day before season end (to prevent creating a tournament on the last day of the season)
    let dayBeforeEnd = null;
    if (seasonEnd) {
      dayBeforeEnd = new Date(seasonEnd);
      dayBeforeEnd.setDate(dayBeforeEnd.getDate() - 1);
    }

    // Check if today is during an active season
    return (
      seasonStart <= today &&
      (!seasonEnd || !dayBeforeEnd || today <= dayBeforeEnd)
    );
  });

  // Check if there's a future season
  const hasNextSeason = seasons.some(
    (season) => new Date(season.startDate) > today,
  );

  // Between seasons if not in a season and there's a next one
  return notInActiveSeason && hasNextSeason;
};

/**
 * Tournament Details tab: Calculates the demographics of participants
 */
export const calculateDemographics = (
  tournament: FrontendTournamentWithDetails,
) => {
  if (!tournament.participants || tournament.participants === 0) {
    return {
      nationalities: [],
      //genders: [],
      //ageGroups: [],
      hasData: false,
    };
  }

  const totalParticipants = tournament.participants;

  // Nationality calculation
  const nationalityCounts: Record<string, number> = {};
  tournament.participantDetails.forEach((participant) => {
    // Default to 'unknown' if nationality is not provided
    const nationality = participant.nationality || 'unknown';
    nationalityCounts[nationality] = (nationalityCounts[nationality] || 0) + 1;
  });

  // Convert to array and sort by count
  let nationalitySorted = Object.entries(nationalityCounts)
    .map(([code, count]) => ({
      label: code !== 'unknown' ? CODE_TO_COUNTRY[code] || code : 'Unknown',
      value: code,
      count,
      percentage: Math.round((count / totalParticipants) * 100),
    }))
    .sort((a, b) => b.count - a.count);

  // Limit to top 5 and add 'Other' category if there are more
  if (nationalitySorted.length > 5) {
    const top5 = nationalitySorted.slice(0, 5);
    const topCount = top5.reduce((sum, item) => sum + item.count, 0);
    const otherCount = totalParticipants - topCount;

    nationalitySorted = top5;

    if (otherCount > 0) {
      nationalitySorted.push({
        label: 'Other',
        value: 'other',
        count: otherCount,
        percentage: Math.round((otherCount / totalParticipants) * 100),
      });
    }
  }

  /* commenting out gender & age calculation for now
      // Gender calculation using actual data
    const genderCounts: Record<string, number> = {
        male: 0,
        female: 0,
        other: 0,
        unknown: 0
    };
    
    tournament.participants.forEach(participant => {
        // Normalize gender to lowercase and handle missing values
        const gender = participant.gender?.toLowerCase() || 'unknown';
        
        // Map to standard categories
        if (gender === 'male' || gender === 'm') {
        genderCounts.male++;
        } else if (gender === 'female' || gender === 'f') {
        genderCounts.female++;
        } else if (gender === 'unknown' || !gender) {
        genderCounts.unknown++;
        } else {
        genderCounts.other++;
        }
    });

    // Format gender data for display
    const genderData = Object.entries(genderCounts)
    .filter(([gender, count]) => count > 0 || gender !== 'unknown') // Keep unknowns only if there are some
    .map(([gender, count]) => ({
    label: gender.charAt(0).toUpperCase() + gender.slice(1),
    value: gender,
    count,
    percentage: Math.round((count / totalParticipants) * 100)
    }));

    // Age calculation using actual data
    const ageRanges = {
        '18-24': { min: 18, max: 24, count: 0 },
        '25-34': { min: 25, max: 34, count: 0 },
        '35-44': { min: 35, max: 44, count: 0 },
        '45+': { min: 45, max: Infinity, count: 0 },
        'Unknown': { min: 0, max: 0, count: 0 }
    };


    tournament.participants.forEach(participant => {
        const age = participant.age;
        
        if (!age) {
        ageRanges['Unknown'].count++;
        } else if (age >= 18 && age <= 24) {
        ageRanges['18-24'].count++;
        } else if (age >= 25 && age <= 34) {
        ageRanges['25-34'].count++;
        } else if (age >= 35 && age <= 44) {
        ageRanges['35-44'].count++;
        } else if (age >= 45) {
        ageRanges['45+'].count++;
        }
    });

    // Format age data for display
    const ageData = Object.entries(ageRanges)
    .filter(([range, data]) => data.count > 0 || range !== 'Unknown') // Keep unknowns only if there are some
    .map(([range, data]) => ({
    label: range,
    value: range.toLowerCase(),
    count: data.count,
    percentage: Math.round((data.count / totalParticipants) * 100)
    }));

    */
  return {
    nationalities: nationalitySorted,
    //genders: genderData,
    //ageGroups: ageData,
    hasData: true,
  };
};

/**
 * Check if a tournament is an FMMA public tournament with no creator
 */
export const isFMMAPublicTournament = (
  tournamentName: string,
  creatorName?: string,
  isPrivate?: boolean,
): boolean => {
  return tournamentName.includes('FMMA') && !creatorName && !isPrivate;
};
